import { useCallback, useEffect, useState } from 'react';

// Fases del intro de login:
//   0 = boot splash (terminal lines)
//   1 = headline glitch reveal
//   2 = HUD on
//   3 = login modal in (form interactivo)
// El zoom va de 2.1 → 1.0 con easing tipo cubic-out.
//
// La intro se salta automáticamente si ya se mostró en esta sesión del browser
// (sessionStorage flag) — primera visita = cinematic, refresh = directo al form.

const SKIP_FLAG = 'dd:login-intro-seen';

type Options = {
  durations?: [number, number, number, number]; // ms por transición de fase
  skipKey?: string;
};

const DEFAULT_DURATIONS: [number, number, number, number] = [
  1100, 1400, 700, 600,
];

export function useIntroSequence(options: Options = {}) {
  const { durations = DEFAULT_DURATIONS, skipKey = SKIP_FLAG } = options;

  const shouldSkip =
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem(skipKey) === '1';

  const [phase, setPhase] = useState<0 | 1 | 2 | 3>(shouldSkip ? 3 : 0);
  const [zoom, setZoom] = useState(shouldSkip ? 1 : 2.1);
  const [runId, setRunId] = useState(0);

  const replay = useCallback(() => {
    setPhase(0);
    setZoom(2.1);
    setRunId((x) => x + 1);
  }, []);

  useEffect(() => {
    if (shouldSkip && runId === 0) return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setPhase(1), durations[0]));
    timers.push(setTimeout(() => setPhase(2), durations[0] + durations[1]));
    timers.push(
      setTimeout(
        () => setPhase(3),
        durations[0] + durations[1] + durations[2],
      ),
    );

    // Marcar como vista al disparar la última fase
    timers.push(
      setTimeout(
        () => {
          try {
            window.sessionStorage.setItem(skipKey, '1');
          } catch {
            // sessionStorage puede no estar disponible (private mode, SSR) — no crítico
          }
        },
        durations[0] + durations[1] + durations[2],
      ),
    );

    const total = durations.reduce((a, b) => a + b, 0);
    const startT = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - startT) / total);
      const eased = 1 - Math.pow(1 - t, 3.5);
      setZoom(2.1 - eased * 1.1);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(raf);
    };
  }, [durations, skipKey, shouldSkip, runId]);

  return { phase, zoom, replay, runId };
}
